/**
 * TemplatePreviewPanel Component (Phase 2 — UX Overhaul)
 *
 * Slide-in side panel (Sheet) for previewing a template's layout
 * with mock data before selecting it. Shows template info,
 * required platform pills, missing integrations warning,
 * and a mini ReportCanvas with generated mock data.
 */

import { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
} from "@provenleap/ui";
import { Warning } from "@phosphor-icons/react";
import { ReportCanvas } from "./ReportCanvas";
import { PlatformPill } from "./TemplateCard";
import {
  getRequiredPlatforms,
  getMissingPlatforms,
} from "../../lib/templateUtils";
import { generateMockData } from "../../lib/mockWidgetData";
import type { ParsedTemplate } from "../../lib/templateUtils";

interface TemplatePreviewPanelProps {
  template: ParsedTemplate | null;
  connectedPlatforms: string[];
  onUseTemplate: (templateId: string) => void;
  onClose: () => void;
}

export function TemplatePreviewPanel({
  template,
  connectedPlatforms,
  onUseTemplate,
  onClose,
}: TemplatePreviewPanelProps) {
  const open = template !== null;

  const requiredPlatforms = useMemo(
    () => (template ? getRequiredPlatforms(template.parsedLayout) : []),
    [template]
  );

  const missingPlatforms = useMemo(
    () => getMissingPlatforms(requiredPlatforms, connectedPlatforms),
    [requiredPlatforms, connectedPlatforms]
  );

  const mockData = useMemo(
    () =>
      template ? generateMockData(template.parsedLayout.widgets) : {},
    [template]
  );

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        className="sm:max-w-[40vw] sm:min-w-[500px] flex flex-col overflow-hidden"
        data-testid="template-preview-panel"
      >
        {template && (
          <>
            <SheetHeader className="shrink-0">
              <SheetTitle data-testid="preview-title">
                {template.name}
              </SheetTitle>
              {template.description && (
                <SheetDescription data-testid="preview-description">
                  {template.description}
                </SheetDescription>
              )}
            </SheetHeader>

            {/* Required platform pills */}
            {requiredPlatforms.length > 0 && (
              <div
                className="flex flex-wrap gap-1.5 mt-3"
                data-testid="preview-platforms"
              >
                {requiredPlatforms.map((platformId) => (
                  <PlatformPill
                    key={platformId}
                    platformId={platformId}
                    isConnected={connectedPlatforms.includes(platformId)}
                  />
                ))}
              </div>
            )}

            {/* Missing integrations warning */}
            {missingPlatforms.length > 0 && (
              <div
                className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800"
                data-testid="preview-missing-warning"
              >
                <Warning
                  size={16}
                  weight="bold"
                  className="shrink-0 mt-0.5"
                />
                <span>
                  {missingPlatforms.length === requiredPlatforms.length
                    ? "All required integrations are missing. Connect them to use this template with full data."
                    : "Some integrations are not connected. The report will show partial data."}
                </span>
              </div>
            )}

            {/* Mini canvas preview */}
            <div
              className="mt-4 flex-1 overflow-auto rounded-md border bg-white"
              data-testid="preview-canvas"
            >
              <div className="origin-top-left scale-[0.6] w-[166.67%]">
                <ReportCanvas
                  widgets={template.parsedLayout.widgets}
                  widgetData={mockData}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div
              className="flex gap-3 mt-4 shrink-0"
              data-testid="preview-actions"
            >
              <Button
                variant="outline"
                className="flex-1"
                onClick={onClose}
                data-testid="preview-back"
              >
                Back to Gallery
              </Button>
              <Button
                variant="brand"
                className="flex-1"
                onClick={() => {
                  onUseTemplate(template.id);
                  onClose();
                }}
                data-testid="preview-use"
              >
                Use This Template
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
